import { ReactNode, useState } from 'react';
import {
  AddAlarmDialogContext,
  AddAlarmDialogContextValue,
  AlarmFormData,
} from './context';

interface AddAlarmDialogProviderProps {
  children: ReactNode;
  selectedMachineIds: string[];
  availableMachines: Array<{ id: string; displayName: string }>;
  initialFormData?: Partial<AlarmFormData>;
  isEditMode?: boolean;
  editingAlarmId?: string | null;
}

export function AddAlarmDialogProvider({
  children,
  selectedMachineIds,
  availableMachines,
  initialFormData,
  isEditMode = false,
  editingAlarmId = null,
}: AddAlarmDialogProviderProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [advancedSettingsExpanded, setAdvancedSettingsExpanded] =
    useState(false);

  const [formData, setFormData] = useState<AlarmFormData>({
    name: '',
    machines: selectedMachineIds,
    signals: [],
    category: '',
    signalUnit: '',
    threshold: '',
    operation: 'greater',
    triggerType: 'minimum',
    thresholdMax: '',
    selectedChannels: [],
    channelSearchQuery: '',
    channelTypeFilters: [],
    triggerLogicType: 'percentage',
    // Advanced settings defaults
    percentageThreshold: '75',
    evaluationWindow: '5',
    cooldownPeriod: '5',
    periodMinutes: '75',
    numberOfSpikes: '5',
    multiSignalSettings: {},
    signalMeta: {},
    ...initialFormData,
  });

  const updateFormData = (updates: Partial<AlarmFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  // For queries, use the machines from formData (which includes edited alarm machines)
  // rather than the prop-based selectedMachineIds
  const effectiveMachineIds = formData.machines;

  const value: AddAlarmDialogContextValue = {
    formData,
    setFormData,
    updateFormData,
    currentStep,
    setCurrentStep,
    advancedSettingsExpanded,
    setAdvancedSettingsExpanded,
    selectedMachineIds: effectiveMachineIds,
    availableMachines,
    isEditMode,
    editingAlarmId,
  };

  return (
    <AddAlarmDialogContext.Provider value={value}>
      {children}
    </AddAlarmDialogContext.Provider>
  );
}
